<!-- W4-5 B 端服务定价申请 -->
<template>
  <div>
    <el-alert :closable="false" class="mb-16px" show-icon type="info">
      机构可在平台基准价 <strong>±20%</strong> 范围内申请调价，提交后由平台在 A 端「机构定价审核」审批。
    </el-alert>

    <el-card shadow="never" class="mb-16px">
      <template #header>
        <div class="card-header">
          <span>可定价服务</span>
          <el-button :loading="optionsLoading" @click="loadOptions">
            <Icon class="mr-4px" icon="ep:refresh" />刷新
          </el-button>
        </div>
      </template>

      <div v-loading="optionsLoading">
        <template v-if="groupedOptions.length">
          <div v-for="group in groupedOptions" :key="group.categoryName" class="category-group mb-16px">
            <div class="category-title">{{ group.categoryName }}</div>
            <el-table :data="group.items" stripe>
              <el-table-column label="服务名称" min-width="160" prop="serviceName" />
              <el-table-column align="right" label="基准价" width="100">
                <template #default="{ row }">¥{{ formatPrice(row.basePrice) }}</template>
              </el-table-column>
              <el-table-column align="right" label="生效价" width="100">
                <template #default="{ row }">
                  {{ row.effectivePrice != null ? `¥${formatPrice(row.effectivePrice)}` : '—' }}
                </template>
              </el-table-column>
              <el-table-column align="center" label="可申请区间" min-width="160">
                <template #default="{ row }">
                  ¥{{ formatPrice(row.minApplyPrice) }} ~ ¥{{ formatPrice(row.maxApplyPrice) }}
                </template>
              </el-table-column>
              <el-table-column align="center" label="最近申请" width="100">
                <template #default="{ row }">
                  <el-tag v-if="row.latestApplyStatus" :type="statusTagType(row.latestApplyStatus)" size="small">
                    {{ statusLabel(row.latestApplyStatus) }}
                  </el-tag>
                  <span v-else class="text-gray-400">—</span>
                </template>
              </el-table-column>
              <el-table-column align="center" fixed="right" label="操作" width="120">
                <template #default="{ row }">
                  <el-button
                    link
                    type="primary"
                    :disabled="row.latestApplyStatus === 'AUDITING'"
                    @click="openApplyDialog(row)"
                  >
                    申请调价
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </template>
        <el-empty v-else description="暂无可定价服务（请确认机构品类与 catalog 已发布服务）" />
      </div>
    </el-card>

    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>我的申请记录</span>
        </div>
      </template>

      <el-form :inline="true" :model="queryParams" class="-mb-15px">
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" class="!w-160px" clearable placeholder="全部">
            <el-option label="待审核" value="AUDITING" />
            <el-option label="已通过" value="PASSED" />
            <el-option label="已驳回" value="REJECTED" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery">
            <Icon class="mr-5px" icon="ep:search" />搜索
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="list" stripe>
        <el-table-column align="center" label="ID" prop="id" width="72" />
        <el-table-column label="服务" min-width="140" prop="serviceName" />
        <el-table-column label="品类" prop="categoryName" width="120" />
        <el-table-column align="right" label="基准价" width="100">
          <template #default="{ row }">¥{{ formatPrice(row.basePrice) }}</template>
        </el-table-column>
        <el-table-column align="right" label="申请价" width="100">
          <template #default="{ row }">¥{{ formatPrice(row.applyPrice) }}</template>
        </el-table-column>
        <el-table-column align="center" label="浮动" width="88">
          <template #default="{ row }">{{ formatRatio(row.floatRatio) }}</template>
        </el-table-column>
        <el-table-column align="center" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="statusTagType(row.status)" size="small">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="驳回原因" min-width="140" prop="rejectReason" show-overflow-tooltip />
        <el-table-column align="center" label="申请时间" prop="createTime" width="170" />
      </el-table>

      <Pagination
        v-model:limit="queryParams.pageSize"
        v-model:page="queryParams.pageNo"
        :total="total"
        @pagination="getList"
      />
    </el-card>

    <el-dialog v-model="applyVisible" title="申请调价" width="480px">
      <el-form ref="applyFormRef" :model="applyForm" :rules="applyRules" label-width="96px">
        <el-form-item label="服务">
          <span>{{ applyForm.serviceName }}</span>
        </el-form-item>
        <el-form-item label="基准价">
          <span>¥{{ formatPrice(applyForm.basePrice) }}</span>
        </el-form-item>
        <el-form-item label="可申请区间">
          <span>¥{{ formatPrice(applyForm.minApplyPrice) }} ~ ¥{{ formatPrice(applyForm.maxApplyPrice) }}</span>
        </el-form-item>
        <el-form-item label="申请价" prop="applyPrice">
          <el-input-number
            v-model="applyForm.applyPrice"
            :min="applyForm.minApplyPrice || 0.01"
            :max="applyForm.maxApplyPrice || undefined"
            :precision="2"
            :step="1"
            class="!w-full"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="applyForm.remark" maxlength="200" placeholder="可选" show-word-limit type="textarea" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="applyVisible = false">取消</el-button>
        <el-button :loading="applySubmitting" type="primary" @click="submitApply">提交申请</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessage } from 'element-plus'
import {
  applyO2oMerchantPricing,
  getO2oMerchantPricingPage,
  getO2oMerchantPricingServiceOptions,
  type O2oPricingMerchantRespVO,
  type O2oPricingServiceOptionVO
} from '@/api/merchant/o2o/pricing'

defineOptions({ name: 'MerchantO2oPricingApply' })

const optionsLoading = ref(false)
const loading = ref(false)
const serviceOptions = ref<O2oPricingServiceOptionVO[]>([])
const list = ref<O2oPricingMerchantRespVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  status: undefined as string | undefined
})

const applyVisible = ref(false)
const applySubmitting = ref(false)
const applyFormRef = ref<FormInstance>()
const applyForm = reactive({
  serviceId: 0,
  serviceName: '',
  basePrice: 0,
  minApplyPrice: 0,
  maxApplyPrice: 0,
  applyPrice: 0,
  remark: ''
})

const applyRules: FormRules = {
  applyPrice: [{ required: true, message: '请输入申请价', trigger: 'blur' }]
}

const groupedOptions = computed(() => {
  const map = new Map<string, O2oPricingServiceOptionVO[]>()
  for (const item of serviceOptions.value) {
    const key = item.categoryName || `品类#${item.categoryId}`
    if (!map.has(key)) {
      map.set(key, [])
    }
    map.get(key)!.push(item)
  }
  return Array.from(map.entries()).map(([categoryName, items]) => ({ categoryName, items }))
})

function formatPrice(val?: number) {
  if (val == null) return '0.00'
  return Number(val).toFixed(2)
}

function formatRatio(val?: number) {
  if (val == null) return '—'
  return `${(Number(val) * 100).toFixed(1)}%`
}

function statusLabel(status?: string) {
  const map: Record<string, string> = {
    AUDITING: '待审核',
    PASSED: '已通过',
    REJECTED: '已驳回'
  }
  return status ? map[status] || status : '—'
}

function statusTagType(status?: string) {
  if (status === 'PASSED') return 'success'
  if (status === 'REJECTED') return 'danger'
  if (status === 'AUDITING') return 'warning'
  return 'info'
}

async function loadOptions() {
  optionsLoading.value = true
  try {
    serviceOptions.value = (await getO2oMerchantPricingServiceOptions()) || []
  } finally {
    optionsLoading.value = false
  }
}

async function getList() {
  loading.value = true
  try {
    const data = await getO2oMerchantPricingPage(queryParams)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

function handleQuery() {
  queryParams.pageNo = 1
  getList()
}

function openApplyDialog(row: O2oPricingServiceOptionVO) {
  applyForm.serviceId = row.serviceId
  applyForm.serviceName = row.serviceName || ''
  applyForm.basePrice = row.basePrice || 0
  applyForm.minApplyPrice = row.minApplyPrice || 0
  applyForm.maxApplyPrice = row.maxApplyPrice || 0
  applyForm.applyPrice = row.effectivePrice ?? row.basePrice ?? 0
  applyForm.remark = ''
  applyVisible.value = true
}

async function submitApply() {
  await applyFormRef.value?.validate()
  applySubmitting.value = true
  try {
    await applyO2oMerchantPricing({
      serviceId: applyForm.serviceId,
      applyPrice: applyForm.applyPrice,
      remark: applyForm.remark || undefined
    })
    ElMessage.success('定价申请已提交，请等待平台审核')
    applyVisible.value = false
    await Promise.all([loadOptions(), getList()])
  } finally {
    applySubmitting.value = false
  }
}

onMounted(async () => {
  await Promise.all([loadOptions(), getList()])
})
</script>

<style scoped lang="scss">
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.category-title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}
</style>
